import axios from 'axios';
import { ChannelType } from '@shopee/shopee-chatbot-agent-chat/dist/types';

import ERROR_HANDLER, { doWhiteListErrorHandle } from './errorHandler';
import { UseLiveChatSDKApp, getConfig, getFromInUrl, isSupportChatbot, getPageBeforeHcInUrl, getChatbotHost, isBank } from '@/config/core';
import { isBankNonLogin, safeGetLiveChatSDKHttpHeaders } from '@/utils/liveChatToken';
import { mdapResponseInterceptors } from '@/utils/mdap/reporter';
import coreStore from '@/stores/core';
import createRequest from './createRequest';
import isInWhiteList, { noneAccessTokenUrlList } from './whitelist';
import API_CONFIG, { ONLY_TRIGGER_WHEN_HAS_CHATBOT_APIS } from './apis';
import reporter, { apiInterceptors } from '@/utils/report';
import { formatFromInUrlToEntrypointName } from '@/utils/instantLoaded/toolsWithoutDeps';
import { capitalizeFirstLetter } from '@/utils/tracking/tracker/ChatbotBITrack';
import { ShopeeEntryPoint } from '@/config/constants';
import userStore from '@/stores/user/index';
import { GetMsgResCode } from '@/utils/constants';
import { ENTRY_POINT } from '@/types/entryPoints';
import { saasDomainInterceptor } from '@/services/interceptors';
import { ClientType } from '@/config/const';
import { SUPPORT_FEATURES_RELATED_API, getSupportFeatures } from '@/utils/featureSwitchControl';
import { getAuthAccountType, initTocWebPromise } from '@/utils/auth';

const CancelToken = axios.CancelToken;

export const fetch = createRequest({ timeout: 10000 });

const noIntentHqsEntries = [ENTRY_POINT.shippingPage];
const countriesHasIntent = getConfig()?.countriesHasIntent || [];

// 并发处理
const pool = new Map();

fetch?.interceptors?.request?.use?.(async (config) => {
  const initStore = coreStore.safeGetStore('initStore');
  const entry = ENTRY_POINT[initStore.from];

  if (
    (countriesHasIntent.includes(COUNTRY) || initStore?.isDirectToLiveChatForNewMarket === false)
    && config.url === API_CONFIG.getInitSession.url
    && !noIntentHqsEntries.includes(entry)
  ) {
    // 目前只有 id 地区有 intent hot questions 的功能
    // 3*3组件 请求
    reporter.apiBatchReport({ type: 'count', value: { label: 'func_head_hqs_load' } });
  }

  if (UseLiveChatSDKApp.is()) {
    const tokens = await safeGetLiveChatSDKHttpHeaders();

    // TODO:
    // remove
    if (ENVIRONMENT !== 'live') {
      console.log('safeGetLiveChatSDKHttpHeaders', {
        ur: config.url,
        tokens: {...tokens}
      });
    }

    // 不传 chat access token
    if (noneAccessTokenUrlList.includes(config.url)) {
      delete tokens['chat-access-token'];
    }

    Object.entries(tokens).map(([key, value]) => {
      config.headers.common[key] = value || '';
    });
  }

  if (isBank() && isBankNonLogin) {
    await initTocWebPromise;
  }

  if (config.url === API_CONFIG.getWebformSubmission.url) {
    config.headers.common['session-key'] = coreStore.safeGetStore('agentStore').sessionKey || '';
  }

  if (config.baseURL.startsWith(`${getChatbotHost()}/api`)) {
    const headers = getChatbotApiHeaders();
    Object.keys(headers).forEach((v) => {
      config.headers.common[v] = headers[v];
    });
  }

  // message接口和solution接口添加固定参数（后期去除）
  if (config.url === '/solution') {
    config.params = {
      ...config.params,
      clarification_flag: 1,
    };
  }

  if (SUPPORT_FEATURES_RELATED_API.includes(config.url)) {
    if (config.url === '/message') {
      const messageExtra = config.data.message_extra ?? {};
      config.data = {
        ...config.data,
        message_extra: {
          clarification_flag: 1,
          ...messageExtra,
        },
        support_features: getSupportFeatures(),
      };
    } else {
      config.params.support_features = getSupportFeatures().join();
    }
  }

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  config.requestStartTime = Date.now();

  return config;
});

fetch?.interceptors?.request?.use?.(saasDomainInterceptor);

fetch?.interceptors?.response?.use?.((response) => {
  const { config: { url, headers: { requestId, connectionLimit } = {} } } = response;
  if (!connectionLimit) {
    return response;
  }
  const connect = pool.get(url) || {};
  delete connect[requestId];
  return response;
});

// 上报处理
fetch?.interceptors.response.use(
  async (response = {}) => {
    apiInterceptors(response);
    mdapResponseInterceptors(response);

    return response;
  },
  async (err) => {
    apiInterceptors(err);

    return Promise.reject(err);
  },
);

// 常规处理
fetch?.interceptors.response.use(
  async (response = {}) => {
    const { status, data: res, config = {} } = response;
    if (status === 200 && res) {
      const CODE = res.code;
      if (CODE === 0) {
        // 请求成功
        const initStore = coreStore.safeGetStore('initStore');
        const entry = ENTRY_POINT[initStore.from];
        if (
          (countriesHasIntent.includes(COUNTRY) || initStore?.isDirectToLiveChatForNewMarket === false)
          && config.url.includes(API_CONFIG.getInitSession.url)
          && !noIntentHqsEntries.includes(entry)
        ) {
          const next_head_intents = res.data?.replies
            ?.map((reply) => reply.message_extra.next_head_intents)
            .filter(Boolean)[0];
          const validQuestions = next_head_intents?.filter((intent) => intent.hotquestion_text);
          if (validQuestions?.length === 9) {
            // 3*3组件 请求成功
            reporter.apiBatchReport({ type: 'count', value: { label: 'func_head_hqs_load_ok' } });
          }
        }

        return Promise.resolve(res.data);
      } else if (CODE === GetMsgResCode.LIVE_CHAT) {
        (config.url.includes(API_CONFIG.getInitSession.url)
          || config.url.includes(API_CONFIG.getFirstInitSession.url))
          && ERROR_HANDLER[CODE]();
      } else {
        // Live Agent Recommender 的 solution 接口失败，直接进线；不局限某个错误码
        if (config.url === API_CONFIG.getIntentSolution.url && config?.params?.trigger_liveagent_recommender) {
          const sessionStore = coreStore.safeGetStore('sessionStore');
          const agentStore = coreStore.safeGetStore('agentStore');
          const { dialogue_id, queue_from, queue_category_id, queue_name, inbound_order_sn } = config.params;
          const dialogueType = sessionStore.dialogueTypeForLiveChat;

          const channelType = await agentStore.chat.getChannelType();
          if (channelType !== queue_from) {
            agentStore.updateChatChannelType(queue_from);
          }

          const { startAgent } = await import('@/utils/agent');
          if (queue_from === ChannelType.inhouse) {
            agentStore.UPDATE_AGENT_CASE_FIELD({
              userType: queue_category_id,
              enquiryType: queue_name,
              inbound_order_sn: inbound_order_sn,
            });
            startAgent(dialogue_id, '', dialogueType, { inboundOrderSn: inbound_order_sn });
          } else {
            startAgent(dialogue_id, queue_name, dialogueType, { inboundOrderSn: inbound_order_sn });
          }
        } else {
          // ERROR
          if (isInWhiteList(config.url, CODE) || config.isErrorWhiteList) {
            const handlerRes = doWhiteListErrorHandle(CODE, res.data);
            if (handlerRes) {
              return handlerRes;
            }
          } else {
            const errorCodeHandler = ERROR_HANDLER[CODE];
            if (errorCodeHandler) {
              errorCodeHandler();
            } else {
              ERROR_HANDLER.NETWORK_ERROR();
            }
          }
        }

        const err = new Error(`Request failed with data code ${CODE || 'UNKNOWN'}`);
        err.response = response;
        return Promise.reject(err);
      }
    } else {
      // 理论上不会进到这里
      const err = new Error(`Request failed with status code ${status || 'UNKNOWN'}`);
      err.response = response;
      return Promise.reject(err);
    }
  },
  (err) => {
    // err本身没有response对象
    const response = err.response || {};
    if (err?.config?.url.includes(API_CONFIG.getUserSwitch.url)) {
      return;
    }

    if (
      response.status >= 500
      && ((err?.config?.url.includes(API_CONFIG.getInitSession.url) && !err?.config?.isErrorWhiteList)
        || err?.config?.url.includes(API_CONFIG.getFirstInitSession.url))
    ) {
      // 5xx server error
      // 监测init_session的异常
      ERROR_HANDLER.SERVER_ERROR();
    } else if (
      isInWhiteList(err?.config?.url || err?.message?.url)
      || err?.config?.isErrorWhiteList
      || err?.message?.isErrorWhiteList
    ) {
      // status code error handler
      doWhiteListErrorHandle(response.status);
    } else {
      if (response.status) {
        const errorCodeHandler = ERROR_HANDLER[response.status];
        if (errorCodeHandler) {
          errorCodeHandler();
        } else {
          ERROR_HANDLER.NETWORK_ERROR();
        }
      } else if (err.message?.includes('timeout')) {
        // timeout error handler
        ERROR_HANDLER.TIMEOUT();
      } else {
        ERROR_HANDLER.NETWORK_ERROR();
      }
    }

    return Promise.reject(err);
  },
);

// 生成器
const withAxios = (apiConfig) => {
  const serviceMap = {};
  // eslint-disable-next-line
  for (const key in apiConfig) {
    if (!apiConfig.hasOwnProperty(key)) {
      continue;
    }

    const { name, url, method, baseUrl, timeout } = apiConfig[key];
    serviceMap[name] = async function (data = {}, config = {}) {
      if (!isSupportChatbot() && ONLY_TRIGGER_WHEN_HAS_CHATBOT_APIS.find((api) => api.name === name) != null) {
        return;
      }

      let key = 'params';
      if (method === 'post' || method === 'put') {
        key = 'data';
      }

      const { connectionLimit = '', isErrorWhiteList } = config;

      const requestId = Date.now();
      const baseURL = baseUrl || '/api/v1/';
      return fetch({
        headers: {
          'shopee-region': COUNTRY,
          requestId,
          connectionLimit,
        },
        baseURL: baseURL.startsWith('/api') ? `${getChatbotHost()}${baseURL}` : baseURL,
        withCredentials: true,
        isErrorWhiteList,
        method,
        url,
        timeout,
        [key]: data,
        cancelToken: new CancelToken(function executor(c) {
          if (!connectionLimit) {
            return;
          }
          const connect = pool.get(url) || {};
          const keys = Object.keys(connect) || [];
          if (keys.length >= connectionLimit) {
            try {
              connect[keys[0]]({ url });
            } catch (err) {
              console.error(err);
            } finally {
              delete connect[keys[0]];
            }
          }
          pool.set(url, {
            ...connect,
            [requestId]: c,
          });
        }),
      });
    };
  }
  return serviceMap;
};

// Chatbot 租户认证需要新增的 header，目前只需要在 /api 和 /report 接口携带
// 如果某些特殊接口无法在 header 上携带，可放入 url query 中，且 中横线 -> 下划线
export function getChatbotApiHeaders() {
  const { region, entrypoint, accountType, botVersion, clientType, entrypointJourney } = getHeaders();
  return {
    region,
    'entrypoint-id': entrypoint,
    'account-type': accountType,
    'bot-exp-group': botVersion,
    'client-type': clientType,
    'entrypoint-journey': entrypointJourney,
    'x-api-source': 'h5',
  };
}

export function getChatbotApiQuery() {
  const { region, entrypoint, accountType, botVersion } = getHeaders();
  return new URLSearchParams({
    region,
    entrypoint_id: entrypoint,
    account_type: accountType,
    bot_exp_group: botVersion,
  }).toString();
}

export const getHeaders = () => {
  const entrypoint = formatFromInUrlToEntrypointName(getFromInUrl());
  const accountType = window.account_type ?? Object.keys(ShopeeEntryPoint).includes(entrypoint) ? 1 : '';

  return {
    region: COUNTRY.toUpperCase(),
    entrypoint: capitalizeFirstLetter(entrypoint),
    accountType,
    botVersion: userStore.abTestingConfigs['bot.intent_base'] ?? 'B_new_version',
    clientType: ClientType.App,
    entrypointJourney: getEntryPointJourney(capitalizeFirstLetter(entrypoint)),
  };
};

export const getEntryPointJourney = (entrypoint) => {
  const pageBeforeHc = getPageBeforeHcInUrl();
  const list = [entrypoint];
  if (pageBeforeHc) {
    const entryPointJourney = formatFromInUrlToEntrypointName(pageBeforeHc);
    list.unshift(capitalizeFirstLetter(entryPointJourney));
  }
  return JSON.stringify(list);
};

export default withAxios;
